import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://haven-hmwq.onrender.com";

export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000,
});

const AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/signup",
  "/auth/google",
  "/auth/logout",
  "/auth/verify-email",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/me",
];

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    const isAuthEndpoint = AUTH_ENDPOINTS.some((ep) => url.includes(ep));

    if (status === 451) {
      window.dispatchEvent(new CustomEvent("haven:legal-required"));
      return Promise.reject(error);
    }

    if (status === 402) {
      const detail =
        error.response?.data?.detail || "You're out of tokens.";
      window.dispatchEvent(
        new CustomEvent("haven:tokens-required", {
          detail: { message: detail, url },
        }),
      );
      return Promise.reject(error);
    }

    if (status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("token");
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    return Promise.reject(error);
  },
);

// ========= Google Auth =========
export async function googleAuth(id_token, email, name, picture, ref = null) {
  const { data } = await api.post("/auth/google", {
    id_token, email, name, picture, ref: ref || null,
  });
  if (data?.token) localStorage.setItem("token", data.token);
  return data;
}

// ========= Auth =========
export async function getMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function logout() {
  try {
    const { data } = await api.post("/auth/logout");
    return data;
  } finally {
    localStorage.removeItem("token");
  }
}

// ========= Legal =========
export async function getLegalDocs() {
  const { data } = await api.get("/legal");
  return data;
}

export async function acceptLegal(privacyVersion, termsVersion) {
  const { data } = await api.post("/accept-legal", {
    privacy_version: privacyVersion,
    terms_version: termsVersion,
  });
  return data;
}

export async function acceptSpecialConsent(version) {
  const { data } = await api.post("/accept-special-consent", { version });
  return data;
}

// ========= Location =========
export async function updateLocation(latitude, longitude, accuracy = null) {
  const { data } = await api.post("/location/update", {
    latitude, longitude, accuracy,
  });
  return data;
}
export async function getLocationStatus() {
  const { data } = await api.get("/location/status");
  return data;
}
export async function ipLocationFallback() {
  const { data } = await api.get("/location/ip-fallback");
  return data;
}

// ========= Profile =========
export async function setupProfile(payload) {
  const p = { ...payload };
  delete p.country; delete p.city;
  const { data } = await api.post("/profile/setup", p);
  return data;
}
export async function getProfile() {
  const { data } = await api.get("/profile");
  return data;
}
export async function updateProfile(payload) {
  const p = { ...payload };
  delete p.country; delete p.city; delete p.latitude; delete p.longitude;
  const { data } = await api.put("/profile", p);
  return data;
}

// ========= Notifications =========
export async function getNotifications() {
  const { data } = await api.get("/notifications");
  return data;
}
export async function markNotificationsRead() {
  const { data } = await api.post("/notifications/read");
  return data;
}
export async function deleteNotification(notificationId) {
  const { data } = await api.delete(`/notifications/${notificationId}`);
  return data;
}
export async function getUnreadNotificationCount() {
  const { data } = await api.get("/notifications/unread-count");
  return data;
}

// ========= Requests =========
export async function getRequests() {
  const { data } = await api.get("/requests");
  return data;
}
export async function respondRequest(requestId, action) {
  const { data } = await api.post(`/requests/${requestId}/respond?action=${action}`);
  return data;
}

// ========= Discovery =========
export async function getDiscoverProfiles(filters = {}) {
  const params = {};
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  if (filters.gender) params.gender = filters.gender;
  if (filters.health_status) params.health_status = filters.health_status;
  if (filters.min_age) params.min_age = filters.min_age;
  if (filters.max_age) params.max_age = filters.max_age;
  if (filters.country) params.country = filters.country;
  if (filters.max_distance) params.max_distance = filters.max_distance;
  if (filters.sexual_orientation) params.sexual_orientation = filters.sexual_orientation;
  const { data } = await api.get("/discover/profiles", { params });
  return data;
}

export async function swipeProfile(swipedId, direction, swipeType = 'dating') {
  const { data } = await api.post("/discover/swipe", {
    swiped_id: swipedId, direction, swipe_type: swipeType,
  });
  return data;
}

export async function getMatches(swipeType = 'dating') {
  const { data } = await api.get("/discover/matches", { params: { swipe_type: swipeType } });
  return data;
}

export async function getMatchMessages(matchId, { afterSeq, beforeSeq, stateSince, limit = 50 } = {}) {
  const params = { limit };
  if (afterSeq != null) params.after_seq = afterSeq;
  if (beforeSeq != null) params.before_seq = beforeSeq;
  if (stateSince) params.state_since = stateSince;
  const { data } = await api.get(`/discover/matches/${matchId}/messages`, { params });
  return data;
}

export async function waitForMatchMessages(matchId, { afterSeq, stateSince, timeout = 25 } = {}) {
  const params = { timeout: String(timeout), limit: "100" };
  if (afterSeq != null) params.after_seq = afterSeq;
  if (stateSince) params.state_since = stateSince;
  const { data } = await api.get(`/discover/matches/${matchId}/wait`, {
    params, timeout: (timeout + 10) * 1000,
  });
  return data;
}

export async function sendMatchMessage(matchId, content, {
  clientMessageId, replyToId, messageType = "text", mediaUrl,
} = {}) {
  const body = { content, message_type: messageType };
  if (clientMessageId) body.client_message_id = clientMessageId;
  if (replyToId) body.reply_to_id = replyToId;
  if (mediaUrl) body.media_url = mediaUrl;
  const { data } = await api.post(`/discover/matches/${matchId}/messages`, body);
  return data;
}

export async function editMatchMessage(matchId, messageId, content) {
  const { data } = await api.put(
    `/discover/matches/${matchId}/messages/${messageId}`, { content });
  return data;
}

export async function deleteMatchMessage(matchId, messageId) {
  const { data } = await api.delete(
    `/discover/matches/${matchId}/messages/${messageId}`);
  return data;
}

export async function reactToMatchMessage(matchId, messageId, emoji) {
  const { data } = await api.post(
    `/discover/matches/${matchId}/messages/${messageId}/react`, { emoji });
  return data;
}

export async function setMatchPinMute(matchId, { pinned, muted } = {}) {
  const body = {};
  if (pinned != null) body.pinned = pinned;
  if (muted != null) body.muted = muted;
  const { data } = await api.put(`/discover/matches/${matchId}/pin-mute`, body);
  return data;
}

export async function sendMatchTyping(matchId) {
  try { await api.post(`/discover/matches/${matchId}/typing`); } catch {}
}

export async function confirmMatchDelivery(matchId, upToSeq) {
  if (!upToSeq || upToSeq <= 0) return;
  try { await api.post(`/discover/matches/${matchId}/delivered`, { up_to_seq: upToSeq }); } catch {}
}

export async function getMatchProfile(matchId) {
  const { data } = await api.get(`/discover/matches/${matchId}/profile`);
  return data;
}

// ========= Location Lists =========
export async function getCountries() {
  const { data } = await api.get("/location/countries");
  return data;
}
export async function getCities(country) {
  const { data } = await api.get("/location/cities", { params: { country } });
  return data;
}

// ========= Stories =========
export async function createStory(payload) {
  const { data } = await api.post("/stories", payload);
  return data;
}
export async function getStories(category = null) {
  const params = category ? { category } : {};
  const { data } = await api.get("/stories", { params });
  return data;
}
export async function getStory(storyId) {
  const { data } = await api.get(`/stories/${storyId}`);
  return data;
}
export async function likeStory(storyId) {
  const { data } = await api.post(`/stories/${storyId}/like`);
  return data;
}
export async function createComment(storyId, content, parentId = null) {
  const { data } = await api.post(`/stories/${storyId}/comments`, {
    content, parent_id: parentId,
  });
  return data;
}

// ========= Token economy =========
export async function earnTokens() {
  const { data } = await api.post("/earn-tokens");
  return data;
}
export async function earnTokensAd() {
  const { data } = await api.post("/earn-tokens-ad");
  return data;
}
export async function earnTokensRewarded() {
  const { data } = await api.post("/earn-tokens-rewarded");
  return data;
}
export async function spendTokens(amount) {
  const { data } = await api.post(`/spend-tokens?amount=${amount}`);
  return data;
}

// ========= Social share earn =========
export async function getShareContext() {
  const { data } = await api.get("/earn-tokens/share/context");
  return data;
}

export async function submitShareClaim(postUrl) {
  const { data } = await api.post("/earn-tokens/share/claim", {
    post_url: postUrl,
  });
  return data;
}

// Admin
export async function getAdminShareClaims(status = "pending") {
  const { data } = await api.get("/admin/share-claims", {
    params: { status },
  });
  return data;
}

export async function approveShareClaim(claimId, adminNotes = "") {
  const { data } = await api.post(
    `/admin/share-claims/${claimId}/approve`,
    { admin_notes: adminNotes }
  );
  return data;
}

export async function rejectShareClaim(claimId, adminNotes = "") {
  const { data } = await api.post(
    `/admin/share-claims/${claimId}/reject`,
    { admin_notes: adminNotes }
  );
  return data;
}

// ========= Public chats =========
export async function getPublicChats() {
  const { data } = await api.get("/public-chats");
  return data;
}
export async function getPublicChatMessages(chatId, { limit, before } = {}) {
  const params = {};
  if (limit) params.limit = limit;
  if (before) params.before = before;
  const { data } = await api.get(`/public-chats/${chatId}/messages`, { params });
  return data;
}
export async function sendPublicChatMessage(chatId, content, chatType, replyToId = null) {
  const body = { content, chat_type: chatType };
  if (replyToId) body.reply_to_id = replyToId;
  const { data } = await api.post(`/public-chats/${chatId}/messages`, body);
  return data;
}
export async function deletePublicChatMessage(messageId) {
  const { data } = await api.delete(`/public-chats/messages/${messageId}`);
  return data;
}

// ========= GPS Helper =========
export async function requestAndUpdateGPS() {
  if (!navigator.geolocation) {
    throw new Error("Geolocation is not supported by your browser");
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        try { resolve(await updateLocation(latitude, longitude, accuracy)); }
        catch (error) { reject(error); }
      },
      (error) => {
        let errorMessage = "Unable to get your location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable GPS in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
        }
        reject(new Error(errorMessage));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

// ========= Email/Password Auth =========
export async function signupEmail(email, password, name) {
  const { data } = await api.post("/auth/signup", { email, password, name });
  return data;
}
export async function loginEmail(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  if (data?.token) localStorage.setItem("token", data.token);
  return data;
}
export async function verifyEmail(token) {
  const { data } = await api.post("/auth/verify-email", { token });
  return data;
}
export async function forgotPassword(email) {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data;
}
export async function resetPassword(token, password) {
  const { data } = await api.post("/auth/reset-password", { token, password });
  return data;
}